import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    ChevronDown,
    ChevronRight,
    BookOpen,
    Users,
    Calendar,
    Scissors,
    Settings,
    CreditCard,
    Package,
    HelpCircle,
    PlayCircle,
    ArrowLeft,
    Lightbulb,
    CheckCircle2,
    ExternalLink,
    Smartphone,
    Shield
} from 'lucide-react';

// ============================================
// HELP CONTENT DATA
// ============================================

interface FAQItem {
    question: string;
    answer: string;
    category: string;
}

interface GuideSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    description: string;
    steps: string[];
}

interface FeatureCard {
    id: string;
    title: string;
    icon: React.ReactNode;
    description: string;
    link: string;
}

const faqData: FAQItem[] = [
    // === GETTING STARTED ===
    {
        question: '¿Cómo instalo Cresta en mi celular?',
        answer: 'Cresta funciona como una App real. En Android: abre cr3sta.com en Chrome y acepta el aviso de "Instalar App". En iPhone: toca el botón Compartir y selecciona "Agregar al inicio". ¡Listo! Tendrás un ícono como cualquier otra aplicación.',
        category: 'Instalación'
    },
    {
        question: '¿Cresta es gratis?',
        answer: 'Sí. Actualmente estamos en Beta Abierta y todas las funcionalidades son 100% gratuitas. Disfruta de empleados, clientes, servicios, agenda e inventario sin límites ni tarjeta de crédito.',
        category: 'General'
    },
    // === EMPLOYEES ===
    {
        question: '¿Cómo creo mi primer empleado?',
        answer: 'Primero debes crear al menos una Especialidad en Configuración → Especialidades. Luego ve a Empleados y haz clic en "Nuevo Empleado". Asigna las especialidades que correspondan.',
        category: 'Empleados'
    },
    {
        question: '¿Cómo gestiono las ausencias de un empleado?',
        answer: 'En la lista de Empleados, haz clic en el ícono de reloj junto al empleado. Podrás registrar vacaciones, incapacidades o permisos personales.',
        category: 'Empleados'
    },
    // === AGENDA ===
    {
        question: '¿Por qué no puedo crear una cita?',
        answer: 'Para crear citas necesitas tener al menos un Empleado y un Servicio configurados. Verifica en las secciones correspondientes que tengas ambos activos.',
        category: 'Agenda'
    },
    {
        question: '¿Cómo cancelo o reprogramo una cita?',
        answer: 'Haz clic sobre la cita en el calendario. Se abrirá un modal con los detalles. Desde allí puedes cambiar el estado a "Cancelada" o modificar la fecha/hora.',
        category: 'Agenda'
    },
    // === CLIENTS ===
    {
        question: '¿Cómo agrego un cliente nuevo?',
        answer: 'Ve a la sección "Clientes" en el menú lateral y haz clic en "Nuevo Cliente". Completa nombre, teléfono y correo electrónico. También puedes agregar clientes directamente al crear una cita.',
        category: 'Clientes'
    },
    // === SERVICES ===
    {
        question: '¿Qué es un servicio "Universal"?',
        answer: 'Un servicio Universal es aquel que no requiere una especialidad específica. Cualquier empleado puede realizarlo, sin importar sus especialidades asignadas.',
        category: 'Servicios'
    },
    // === INVENTORY & SUPPLIERS ===
    {
        question: '¿Cómo registro un proveedor?',
        answer: 'Ve a Inventario → pestaña Proveedores. Haz clic en "Nuevo Proveedor" y registra nombre, contacto, teléfono y email. Luego podrás asociar productos a ese proveedor.',
        category: 'Inventario'
    },
    {
        question: '¿Puedo tener múltiples ubicaciones de inventario?',
        answer: 'Sí. Ve a Inventario → pestaña Ubicaciones. Puedes crear ubicaciones como "Bodega", "Exhibición", "Salón Principal", etc. y gestionar el stock de cada una por separado.',
        category: 'Inventario'
    },
    // === CONFIG ===
    {
        question: '¿Cómo cambio el logo de mi salón?',
        answer: 'Desde tu perfil (arriba a la derecha), selecciona "Configuración". En la pestaña "Marca" podrás subir tu logo y definir los colores de tu marca.',
        category: 'Configuración'
    },
    {
        question: '¿Qué son las Especialidades?',
        answer: 'Las Especialidades definen las habilidades de tu equipo (ej: Corte, Color, Manicure). Se usan para asignar qué empleados pueden realizar cada servicio.',
        category: 'Configuración'
    },
    // === FINANCES ===
    {
        question: '¿Cómo veo mis ingresos del mes?',
        answer: 'Ve a la sección Finanzas. Allí encontrarás un resumen de ventas, comisiones por empleado y gráficos de tendencias. Puedes filtrar por día, semana, mes o año.',
        category: 'Finanzas'
    },
    // === SECURITY ===
    {
        question: '¿Mis datos están seguros?',
        answer: 'Sí. Usamos encriptación SSL, autenticación segura y aislamiento de datos por salón (Row Level Security). Tus datos nunca se comparten con otros salones.',
        category: 'Seguridad'
    },
    // === PUBLIC BOOKING ===
    {
        question: '¿Cómo obtengo mi link de reservas públicas?',
        answer: 'Cada salón tiene un link único: cr3sta.com/book/tu-salon. Puedes encontrarlo y compartirlo desde el Dashboard o en Configuración → Seguridad.',
        category: 'Reservas Públicas'
    },
    {
        question: '¿Cómo funciona la aprobación de reservas?',
        answer: 'Si tienes activo el modo "Aprobación Requerida", las citas no se agendan automáticamente. Recibirás una notificación y deberás aprobarlas desde el Dashboard en la sección "Reservas Públicas" para que queden confirmadas.',
        category: 'Reservas Públicas'
    },
    {
        question: '¿Por qué pedir verificación de teléfono?',
        answer: 'La verificación por SMS (OTP) asegura que el cliente es real y reduce el "no-show" y el spam. El cliente recibe un código de 6 dígitos que debe ingresar para completar su solicitud.',
        category: 'Reservas Públicas'
    },
    {
        question: '¿Qué es el Modo VIP?',
        answer: 'Es un nivel de privacidad donde solo los clientes que conocen un código especial (configurado por ti) pueden ver tu agenda y solicitar citas.',
        category: 'Reservas Públicas'
    },
    // === INTEGRATIONS ===
    {
        question: '¿Puedo usar mi propio número de WhatsApp?',
        answer: 'Sí. Puedes conectar tu propio WhatsApp (Business o Personal) escaneando el código QR en Configuración → Integraciones. El sistema usará tu celular para enviar las notificaciones.',
        category: 'Integraciones'
    },
    // === SMART NOTIFICATIONS ===
    {
        question: '¿Qué es la confirmación bidireccional?',
        answer: 'Con las Notificaciones Inteligentes, cuando se agenda una cita o llega un recordatorio, el cliente recibe un link directo por WhatsApp para Confirmar o Cancelar su asistencia con un solo toque. El sistema actualiza el estado automáticamente.',
        category: 'Notificaciones'
    },
    {
        question: '¿Cuándo se envían los recordatorios automáticos?',
        answer: 'El sistema envía dos recordatorios: uno 24 horas antes de la cita y otro 1 hora antes. Ambos incluyen un enlace para que el cliente confirme o cancele directamente desde el mensaje de WhatsApp.',
        category: 'Notificaciones'
    },
    {
        question: '¿Qué pasa si el cliente cancela desde el link?',
        answer: 'Si el cliente toca "Cancelar" desde el mensaje de WhatsApp, la cita se cancela automáticamente en el sistema y el dueño del salón puede verlo en la Agenda y en las Reservas Públicas.',
        category: 'Notificaciones'
    },
    // === STYLIST MICRO-PAGES ===
    {
        question: '¿Qué es una Micro-página de Estilista?',
        answer: 'Cada estilista tiene su propio link de reserva personalizado: cr3sta.com/book/tu-salon/e/[id-del-empleado]. Cuando un cliente usa ese link, la página de reserva salta directamente al paso de elegir fecha/hora, sin pasar por la selección de empleado.',
        category: 'Reservas Públicas'
    },
    {
        question: '¿Cómo obtengo el link de reserva de un estilista específico?',
        answer: 'Próximamente, podrás copiar el link de cada empleado desde su perfil en la sección Empleados. Por ahora el formato es: cr3sta.com/book/[tu-salon]/e/[id-del-empleado].',
        category: 'Reservas Públicas'
    }
];

const quickStartGuide: GuideSection[] = [
    {
        id: 'step-1',
        title: 'Configura tu Salón',
        icon: <Settings className="w-6 h-6" />,
        description: 'Define la información básica de tu negocio.',
        steps: [
            'Accede a Configuración desde tu perfil (arriba a la derecha)',
            'Completa el nombre, dirección y zona horaria de tu salón',
            'Sube tu logo en la pestaña "Marca"'
        ]
    },
    {
        id: 'step-2',
        title: 'Crea Especialidades',
        icon: <Lightbulb className="w-6 h-6" />,
        description: 'Define las habilidades que ofrece tu equipo.',
        steps: [
            'Ve a Configuración → Catálogos → Especialidades',
            'Crea al menos una (ej: "Corte", "Color", "Manicure")',
            'Estas aparecerán al crear empleados y servicios'
        ]
    },
    {
        id: 'step-3',
        title: 'Agrega tu Equipo',
        icon: <Users className="w-6 h-6" />,
        description: 'Registra a tus estilistas y colaboradores.',
        steps: [
            'Ve a la sección "Empleados" en el menú lateral',
            'Crea un nuevo empleado con nombre, rol y especialidades',
            'Define su porcentaje de comisión si aplica'
        ]
    },
    {
        id: 'step-4',
        title: 'Define tus Servicios',
        icon: <Scissors className="w-6 h-6" />,
        description: 'Crea tu catálogo de servicios con precios.',
        steps: [
            'Ve a la sección "Servicios"',
            'Agrega cada servicio con nombre, precio y duración',
            'Opcionalmente, asigna una especialidad requerida'
        ]
    },
    {
        id: 'step-5',
        title: '¡Agenda tu Primera Cita!',
        icon: <Calendar className="w-6 h-6" />,
        description: 'Ya estás listo para operar.',
        steps: [
            'Ve a la "Agenda" y haz clic en cualquier hora',
            'Selecciona el cliente, empleado y servicio',
            'Confirma la cita y ¡listo!'
        ]
    },
    {
        id: 'step-6',
        title: 'Activa Reservas Online',
        icon: <Shield className="w-6 h-6" />,
        description: 'Permite que tus clientes reserven por sí mismos.',
        steps: [
            'Ve a Configuración → Seguridad',
            'Configura el Modo de Reserva (Abierto o Aprobación)',
            'Copia tu link público y compártelo en tu Instagram o WhatsApp'
        ]
    },
    {
        id: 'step-7',
        title: 'Conecta WhatsApp',
        icon: <Smartphone className="w-6 h-6" />,
        description: 'Envía confirmaciones automáticas a tus clientes.',
        steps: [
            'Ve a Configuración → Integraciones',
            'Haz clic en "Conectar WhatsApp" para generar el QR',
            'Escanea el código con tu celular (WhatsApp → Dispositivos vinculados)'
        ]
    },
    {
        id: 'step-8',
        title: 'Activa Confirmación Bidireccional',
        icon: <Shield className="w-6 h-6" />,
        description: 'Permite que tus clientes confirmen o cancelen desde WhatsApp.',
        steps: [
            'Con WhatsApp conectado, los mensajes de cita incluyen un link automático',
            'El cliente puede confirmar ✅ o cancelar ❌ con un solo toque',
            'El estado de la cita se actualiza automáticamente en la Agenda'
        ]
    }
];

const featureCards: FeatureCard[] = [
    {
        id: 'agenda',
        title: 'Agenda Inteligente',
        icon: <Calendar className="w-8 h-8 text-emerald-600" />,
        description: 'Calendario visual con vistas de día, semana y mes. Filtros avanzados por empleado y servicio.',
        link: '/app/agenda'
    },
    {
        id: 'clients',
        title: 'Gestión de Clientes',
        icon: <Users className="w-8 h-8 text-pink-600" />,
        description: 'Historial de visitas, datos de contacto y búsqueda rápida de tu clientela.',
        link: '/app/clients'
    },
    {
        id: 'employees',
        title: 'Gestión de Equipo',
        icon: <Users className="w-8 h-8 text-blue-600" />,
        description: 'Administra horarios, especialidades, comisiones y ausencias de cada colaborador.',
        link: '/app/employees'
    },
    {
        id: 'services',
        title: 'Catálogo de Servicios',
        icon: <Scissors className="w-8 h-8 text-purple-600" />,
        description: 'Define precios, duración y requisitos de cada servicio que ofreces.',
        link: '/app/services'
    },
    {
        id: 'finances',
        title: 'Finanzas y Reportes',
        icon: <CreditCard className="w-8 h-8 text-amber-600" />,
        description: 'Visualiza ventas, comisiones y tendencias de tu negocio.',
        link: '/app/finances'
    },
    {
        id: 'inventory',
        title: 'Inventario y Proveedores',
        icon: <Package className="w-8 h-8 text-rose-600" />,
        description: 'Control de stock, múltiples ubicaciones, proveedores y transferencias.',
        link: '/app/inventory'
    },
    {
        id: 'settings',
        title: 'Configuración',
        icon: <Settings className="w-8 h-8 text-gray-600" />,
        description: 'Personaliza tu salón: logo, colores, especialidades y categorías.',
        link: '/app/settings'
    },
    {
        id: 'public-booking',
        title: 'Reservas Públicas',
        icon: <Shield className="w-8 h-8 text-emerald-500" />,
        description: 'Link público, Micro-páginas por estilista, Modo VIP y aprobaciones.',
        link: '/app/settings'
    },
    {
        id: 'whatsapp',
        title: 'Notificaciones Inteligentes',
        icon: <Smartphone className="w-8 h-8 text-emerald-600" />,
        description: 'Recordatorios automáticos 24h y 1h antes. Confirmación bidireccional desde WhatsApp.',
        link: '/app/settings?tab=integrations'
    }
];

// ============================================
// COMPONENTS
// ============================================

const FAQAccordion = ({ items }: { items: FAQItem[] }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    return (
        <div className="space-y-3">
            {items.map((item, index) => (
                <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                    <button
                        className="w-full px-5 py-4 flex items-center justify-between text-left"
                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                {item.category}
                            </span>
                            <span className="font-medium text-gray-900">{item.question}</span>
                        </div>
                        {openIndex === index ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                    </button>
                    {openIndex === index && (
                        <div className="px-5 pb-4 pt-0 text-gray-600 leading-relaxed border-t border-gray-50">
                            {item.answer}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const GuideCard = ({ section }: { section: GuideSection }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all">
            <div
                className="flex items-start gap-4 cursor-pointer"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
                    {section.icon}
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{section.description}</p>
                </div>
                {isOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 mt-1" />
                ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 mt-1" />
                )}
            </div>

            {isOpen && (
                <div className="mt-4 pl-16 space-y-2">
                    {section.steps.map((step, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{step}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================
// MAIN PAGE
// ============================================

const HelpPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'guide' | 'faq' | 'features'>('guide');

    // Filter FAQ based on search
    const filteredFAQ = useMemo(() => {
        if (!searchQuery.trim()) return faqData;
        const query = searchQuery.toLowerCase();
        return faqData.filter(
            item =>
                item.question.toLowerCase().includes(query) ||
                item.answer.toLowerCase().includes(query) ||
                item.category.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    // Filter guide based on search
    const filteredGuide = useMemo(() => {
        if (!searchQuery.trim()) return quickStartGuide;
        const query = searchQuery.toLowerCase();
        return quickStartGuide.filter(
            section =>
                section.title.toLowerCase().includes(query) ||
                section.description.toLowerCase().includes(query) ||
                section.steps.some(step => step.toLowerCase().includes(query))
        );
    }, [searchQuery]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
            {/* Header */}
            <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/app"
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </Link>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <HelpCircle className="w-6 h-6 text-emerald-600" />
                                Centro de Ayuda
                            </h1>
                            <p className="text-sm text-gray-500">Todo lo que necesitas para dominar Cresta</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Search */}
            <section className="bg-gradient-to-r from-emerald-600 to-teal-600 py-12 px-4">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        ¿En qué podemos ayudarte?
                    </h2>
                    <p className="text-emerald-100 mb-6">
                        Busca en nuestra guía, FAQ o explora las funcionalidades
                    </p>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-400 shadow-lg focus:outline-none focus:ring-4 focus:ring-emerald-300"
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                </div>
            </section>

            {/* Tabs */}
            <div className="max-w-5xl mx-auto px-4 -mt-6">
                <div className="bg-white rounded-xl shadow-md p-1 flex gap-1">
                    <button
                        onClick={() => setActiveTab('guide')}
                        className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'guide'
                            ? 'bg-emerald-600 text-white shadow'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <BookOpen className="w-5 h-5" />
                        <span className="hidden sm:inline">Guía de Inicio</span>
                        <span className="sm:hidden">Guía</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('faq')}
                        className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'faq'
                            ? 'bg-emerald-600 text-white shadow'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <HelpCircle className="w-5 h-5" />
                        <span className="hidden sm:inline">Preguntas Frecuentes</span>
                        <span className="sm:hidden">FAQ</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('features')}
                        className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'features'
                            ? 'bg-emerald-600 text-white shadow'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <PlayCircle className="w-5 h-5" />
                        <span className="hidden sm:inline">Funcionalidades</span>
                        <span className="sm:hidden">Features</span>
                    </button>
                </div>
            </div>

            {/* Content */}
            <main className="max-w-5xl mx-auto px-4 py-8">
                {/* Quick Start Guide */}
                {activeTab === 'guide' && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-6">
                            <BookOpen className="w-6 h-6 text-emerald-600" />
                            <h2 className="text-xl font-bold text-gray-900">Guía de Inicio Rápido</h2>
                        </div>

                        {filteredGuide.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                No se encontraron resultados para "{searchQuery}"
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {filteredGuide.map((section, idx) => (
                                    <div key={section.id} className="relative">
                                        {/* Step Number Badge */}
                                        <div className="absolute -left-3 top-5 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md z-10">
                                            {idx + 1}
                                        </div>
                                        <div className="ml-6">
                                            <GuideCard section={section} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pro Tips */}
                        <div className="mt-8 space-y-4">
                            <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-4">
                                <span className="text-2xl">📲</span>
                                <div>
                                    <h4 className="font-semibold text-emerald-900">Instala Cresta en tu celular</h4>
                                    <p className="text-sm text-emerald-800 mt-1">
                                        Cresta funciona como una App real. En Android acepta el aviso de "Instalar App".
                                        En iPhone toca Compartir → "Agregar al inicio". ¡Tendrás acceso rápido y notificaciones!
                                    </p>
                                </div>
                            </div>
                            <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-4">
                                <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-amber-900">Repite el Tutorial</h4>
                                    <p className="text-sm text-amber-800 mt-1">
                                        Puedes ver el tutorial interactivo en cualquier momento desde tu perfil → "Ver Tutorial".
                                        Te guiará paso a paso por la interfaz.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* FAQ */}
                {activeTab === 'faq' && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-6">
                            <HelpCircle className="w-6 h-6 text-emerald-600" />
                            <h2 className="text-xl font-bold text-gray-900">
                                Preguntas Frecuentes
                                {searchQuery && ` (${filteredFAQ.length} resultados)`}
                            </h2>
                        </div>

                        {filteredFAQ.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                No se encontraron preguntas para "{searchQuery}"
                            </div>
                        ) : (
                            <FAQAccordion items={filteredFAQ} />
                        )}
                    </div>
                )}

                {/* Features Grid */}
                {activeTab === 'features' && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 mb-6">
                            <PlayCircle className="w-6 h-6 text-emerald-600" />
                            <h2 className="text-xl font-bold text-gray-900">Explora las Funcionalidades</h2>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {featureCards.map(card => (
                                <Link
                                    key={card.id}
                                    to={card.link}
                                    className="group bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-emerald-50 transition-colors">
                                            {card.icon}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors flex items-center gap-2">
                                                {card.title}
                                                <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </h3>
                                            <p className="text-sm text-gray-500 mt-1">{card.description}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            {/* Developer Section (for Beta/Support) */}
            <section className="max-w-5xl mx-auto px-4 py-12 mt-12 border-t border-gray-100/50">
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50/30 rounded-2xl p-8 border border-blue-100 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Herramientas de Diagnóstico</h3>
                        <p className="text-gray-600">Si experimentas problemas técnicos, puedes realizar una prueba de diagnóstico para nuestro equipo de soporte.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={() => {
                                import('../../sentry').then(m => m.triggerSentryTest());
                                alert("Diagnóstico enviado a Sentry. Revisa la consola para más detalles.");
                            }}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-medium flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
                        >
                            <span>Probar Conexión Sentry</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-100 py-8 mt-12">

                <div className="max-w-5xl mx-auto px-4 text-center">
                    <p className="text-gray-500 text-sm">
                        ¿No encontraste lo que buscabas?{' '}
                        <a
                            href="mailto:soporte@cr3sta.com"
                            className="text-emerald-600 hover:underline font-medium"
                        >
                            Contáctanos
                        </a>
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default HelpPage;
